/**
 * K-12 districts domain — utility functions and shared types.
 *
 * Re-exports generated entity types under domain-friendly aliases plus
 * the typed adapters that bridge the Kubb-generated shapes to the
 * legacy props expected by `DECommonTable`, `DemographicsSection`, etc.
 *
 * The cross-domain adapters live here (rather than in component files)
 * so the casts are documented and centralized.
 */

import type { K12ChainProxy } from '../../models/k12'
import type {
  StandardPaginatedResponse,
  StandardSummaryResponse,
} from '../../models/paginated-response'
import type { AddressInfo } from '../../models/restaurant'
import { getK12DistrictsSummary as getK12DistrictsSummaryClient } from '../generated/clients/getK12DistrictsSummary'
import { listK12Districts as listK12DistrictsClient } from '../generated/clients/listK12Districts'
import type { GetK12DistrictsSummaryQueryParams } from '../generated/types/GetK12DistrictsSummary'
import type { K12DistrictDetailNested } from '../generated/types/K12DistrictDetailNested'
import type { ListK12DistrictsQueryParams } from '../generated/types/ListK12Districts'

// ── Domain entity type re-exports ───────────────────────────────────

export type { K12DistrictListItem } from '../generated/types/K12DistrictListItem'
export type { K12DistrictListItemNested } from '../generated/types/K12DistrictListItemNested'
export type { K12DistrictDetail } from '../generated/types/K12DistrictDetail'
export type { K12DistrictDetailNested } from '../generated/types/K12DistrictDetailNested'
export type { K12LabelValue } from '../generated/types/K12LabelValue'
export type { PaginatedK12DistrictListItemList } from '../generated/types/PaginatedK12DistrictListItemList'

// ── Typed adapters for legacy component props ───────────────────────

/**
 * List K-12 districts shaped for `DECommonTable.apiGetListAction`.
 *
 * The table component is generic over `K12ChainProxy` (the legacy
 * model the column definitions are typed against). At runtime the
 * generated `K12DistrictListItem` has the same fields the columns
 * read, so the cast is a type-only bridge — encapsulated here so
 * call sites stay clean.
 */
export async function listK12DistrictsForTable(
  params: ListK12DistrictsQueryParams = {}
): Promise<StandardPaginatedResponse<K12ChainProxy>> {
  return listK12DistrictsClient(
    params
  ) as unknown as StandardPaginatedResponse<K12ChainProxy>
}

/**
 * Get the K-12 summary count shaped for `DECommonTable.apiGetSummaryAction`.
 *
 * The legacy `StandardSummaryResponse` has a second `alt_count` field
 * the K-12 backend never populates; the table treats the missing
 * value as `undefined`. The cast is centralized here so the table
 * component's prop signature stays unchanged.
 */
export async function getK12DistrictsSummaryForTable(
  params: GetK12DistrictsSummaryQueryParams = {}
): Promise<StandardSummaryResponse> {
  return getK12DistrictsSummaryClient(
    params
  ) as unknown as StandardSummaryResponse
}

/**
 * Convert the generated `K12DistrictDetailNested.address_info` (a
 * `ZipCode` shape) into the legacy `AddressInfo` interface that
 * `DemographicsSection` accepts.
 *
 * `models/restaurant.AddressInfo` declares every demographic field as
 * required & non-null; the generated `ZipCode` correctly types them as
 * optional. `DemographicsSection` already defends against missing
 * fields with `field || 0`, so the runtime data is compatible. This
 * helper makes the type bridge explicit instead of forcing every
 * call site to repeat `as unknown as AddressInfo`.
 */
export function toLegacyAddressInfo(
  addressInfo: K12DistrictDetailNested['address_info'] | null | undefined
): AddressInfo | undefined {
  if (!addressInfo) return undefined
  return addressInfo as unknown as AddressInfo
}
